/* eslint-disable react-hooks/exhaustive-deps */
import { Form, Input } from "antd";
import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import { getConfigAppPartType } from "@lhd/features/Apps/utils";
import { Styles } from "./styled";
import isNil from "lodash/isNil";
import { UNIT_OF_MEASURE_TYPE } from "@lhd/features/Apps/AppForm/constants";
import { getRuleValidation } from "@lhd/components/CloneApp/fieldUtils";

const STRICT_NUMBER_ONLY_FIELDS = [
  "transportation_expense",
  "accommodation_expense_0",
  "daily_allowance",
  "preparation_expense",
  "actual_expense_reimbursement",
];

interface IProps {
  item: AppPartType;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  styleColor?: React.CSSProperties;
  namePath?: string | string[];
  isDisabled?: boolean;
}

const InputNumber = (props: IProps) => {
  const { item, styleColor = {}, namePath, isDisabled } = props;
  const shouldShowUnit = true;
  const { maxChar, minChar } = getConfigAppPartType(item);

  const isStrictNumberOnly = STRICT_NUMBER_ONLY_FIELDS.includes(
    item.field_code || "",
  );

  const addonBefore =
    item.unit_of_measure_type === UNIT_OF_MEASURE_TYPE.frefix ||
    item.unit_of_measure_type === UNIT_OF_MEASURE_TYPE.frefix_suffix
      ? item.unit_of_measure
      : "";

  const addonAfter =
    item.unit_of_measure_type === UNIT_OF_MEASURE_TYPE.suffix ||
    !item.unit_of_measure_type ||
    item.unit_of_measure_type === UNIT_OF_MEASURE_TYPE.frefix_suffix
      ? item.unit_of_measure
      : "";

  const convertFullWidthToHalfWidth = (input: string) => {
    return input
      ?.replace(/[\uFF10-\uFF19]/g, (match) => {
        return String.fromCharCode(match.charCodeAt(0) - 0xfee0);
      })
      ?.replace(/\uFF0C/g, ",")
      ?.replace(/\uFF0E/g, ".")
      ?.replace(/\uFF0D/g, "-");
  };

  const form = Form.useFormInstance();

  const handleStrictBlur = () => {
    if (!isStrictNumberOnly || !namePath) return;
    const rawValue: string = form.getFieldValue(namePath) ?? "";
    if (rawValue === "") return;

    let isNegative = false;
    const valStr = String(rawValue).trim();
    if (valStr.startsWith("-")) {
      isNegative = true;
    }

    const onlyNums = valStr.replace(/[^\d]/g, "");
    if (onlyNums === "") {
      form.setFieldValue(namePath, isNegative ? "-" : "");
      form.validateFields([namePath]);
      return;
    }

    const num = Number(onlyNums);
    const formatted = (isNegative ? "-" : "") + num.toLocaleString("en-US");

    form.setFieldValue(namePath, formatted);
    form.validateFields([namePath]);
  };

  return (
    <Styles className={!shouldShowUnit ? `should_hidden_unit` : ""}>
      <Form.Item
        name={namePath}
        rules={[
          ...getRuleValidation(item),
          {
            message:
              "このフィールドは数字、カンマ、ピリオド、マイナス記号のみ許可されます",
            validator: (_: any, value: string) => {
              if (!value || value === "") {
                return Promise.resolve();
              }

              const convertValue = convertFullWidthToHalfWidth(value);

              if (convertValue === undefined) {
                return Promise.resolve();
              }

              if (!/^[-\d.,\s]*$/.test(convertValue)) {
                return Promise.reject();
              }

              return Promise.resolve();
            },
          },
          {
            message: "有効な数字を入力してください。",
            validator: (_: any, value: string) => {
              if (!value || value === "") {
                return Promise.resolve();
              }
              const convertValue = convertFullWidthToHalfWidth(value);
              if (convertValue === undefined) {
                return Promise.resolve();
              }

              // disable multiple dot
              if (/\.{2,}/.test(convertValue)) {
                return Promise.reject();
              }

              // disable multiple comma
              if (/,{2,}/.test(convertValue)) {
                return Promise.reject();
              }

              // allow a lone "-" (user is still typing a negative number)
              if (convertValue === "-") {
                return Promise.resolve();
              }

              // check valid number format
              if (
                !/^-?\d{1,3}(,\d{3})*(\.\d*)?$|^-?\d+(\.\d*)?$/.test(
                  convertValue,
                )
              ) {
                return Promise.reject();
              }
              return Promise.resolve();
            },
          },
          {
            validator: (_: any, value: string) => {
              if (!value) return Promise.resolve();
              const valueClean = Number(value.replaceAll(",", ""));
              if (!isNil(minChar) && !isNil(maxChar)) {
                if (valueClean > maxChar || valueClean < minChar)
                  return Promise.reject(
                    `その値は${minChar}以上${maxChar}以下でなければなりません。`,
                  );
              } else if (!isNil(minChar)) {
                if (valueClean < minChar)
                  return Promise.reject(
                    `その値は${minChar}以上でなければなりません。`,
                  );
              } else if (!isNil(maxChar)) {
                if (valueClean > maxChar)
                  return Promise.reject(
                    `その値は${maxChar}以下でなければなりません。`,
                  );
              }
              return Promise.resolve();
            },
          },
        ]}
        normalize={(value) => convertFullWidthToHalfWidth(value)}
      >
        <Input
          className="align-right"
          addonBefore={addonBefore}
          addonAfter={addonAfter}
          placeholder=" "
          style={styleColor}
          disabled={isDisabled}
          onPressEnter={(e) => e.preventDefault()}
          onBlur={isStrictNumberOnly ? handleStrictBlur : undefined}
        />
      </Form.Item>
    </Styles>
  );
};

export default InputNumber;
